import { useEffect, useState } from 'react';
import { GetOneResult, useDataProvider, useNotify } from 'react-admin';
import { RegionDto, SubdivisionDto } from '@vatos-pas/common';

import {
  CostStructurePercentageField,
  resolveAllowPayoutAmountEditing,
  resolveCostStructurePercentage,
} from 'utils/costStructurePercentage';

export type UseCostStructurePercentageInput = {
  // Fetch only while the selection actually pays by percentage; the value
  // resets to undefined whenever this goes false so a stale percentage never
  // leaks into a later selection.
  active: boolean;
  subdivisionId: string | undefined;
  regionId: string | undefined;
  field: CostStructurePercentageField;
};

export type UseCostStructurePercentageResult = {
  // Resolved Cost Structure percentage, undefined while inactive, before the
  // records load, or when the governing value is absent.
  percentage: number | undefined;
  // Resolved Allow Payout Amount Editing toggle. Fails safe to false while
  // inactive or before both records load — editing is never granted by
  // accident.
  allowPayoutAmountEditing: boolean;
};

const INACTIVE_RESULT: UseCostStructurePercentageResult = {
  percentage: undefined,
  allowPayoutAmountEditing: false,
};

// Resolves the Cost Structure percentage (Region/Subdivision OFF / ON+Fixed /
// ON+Offset rules) and the Allow Payout Amount Editing toggle for a
// percentage-paid repair payment, from the same Subdivision/Region pair.
// Shared by the Add Contractor dialogs on the master sheet and the job repairs
// sheet.
export const useCostStructurePercentage = ({
  active,
  subdivisionId,
  regionId,
  field,
}: UseCostStructurePercentageInput): UseCostStructurePercentageResult => {
  const [result, setResult] =
    useState<UseCostStructurePercentageResult>(INACTIVE_RESULT);
  const dataProvider = useDataProvider();
  const notify = useNotify();

  useEffect(() => {
    if (!active || !subdivisionId || !regionId) {
      setResult(INACTIVE_RESULT);
      return;
    }

    let cancelled = false;

    const fetchCostStructure = async () => {
      try {
        const [subdivisionResult, regionResult] = await Promise.all([
          dataProvider.getOne('subdivision', {
            id: subdivisionId,
          }) as Promise<GetOneResult<SubdivisionDto>>,
          dataProvider.getOne('region', {
            id: regionId,
          }) as Promise<GetOneResult<RegionDto>>,
        ]);

        if (cancelled) return;

        setResult({
          percentage: resolveCostStructurePercentage({
            region: regionResult.data,
            subdivision: subdivisionResult.data,
            field,
          }),
          allowPayoutAmountEditing: resolveAllowPayoutAmountEditing({
            region: regionResult.data,
            subdivision: subdivisionResult.data,
          }),
        });
      } catch (error) {
        if (cancelled) return;
        setResult(INACTIVE_RESULT);
        notify('Failed to load cost structure, try again', 'error');
      }
    };

    fetchCostStructure();

    return () => {
      cancelled = true;
    };
  }, [active, subdivisionId, regionId, field]);

  return result;
};
